import {
  getShotSourceImage,
  sortEditorShotsForDisplay,
} from "@beleef/editor-shot";
import { useCallback, useEffect, useMemo, useState } from "react";
import { showToast } from "components/ui/toast";
import {
  buildRevisionRequestItems,
  isExtrasRawBatch,
  isPhoneRawBatch,
} from "utils/prepareMarketing";

// The editor classified (and the contractor confirmed) every shot. Send that
// category — and the shot id, so the backend can look it up when a row has
// no source image — with each uploaded web/print photo; the marketing link
// otherwise receives the photos uncategorised.
const shotFields = (row) => ({
  ...(row?.rawImage?.category ? { category: row.rawImage.category } : {}),
  ...(row?.shotId ? { shotId: row.shotId } : {}),
});

const MIN_PRINT_MB = 5;

const FULL_CROP_RECT = { x: 0, y: 0, width: 1, height: 1 };

const isAdjustedCropRect = (rect) => {
  if (!rect) return false;
  return (
    Math.abs(Number(rect.x) || 0) > 0.01 ||
    Math.abs(Number(rect.y) || 0) > 0.01 ||
    Math.abs((Number(rect.width) || 1) - 1) > 0.01 ||
    Math.abs((Number(rect.height) || 1) - 1) > 0.01
  );
};

const resolveCropRect = (web, print) => {
  const fromWeb = web?.webCropRect;
  const fromPrint = print?.webCropRect;
  if (fromWeb && typeof fromWeb === "object") return fromWeb;
  if (fromPrint && typeof fromPrint === "object") return fromPrint;
  return { ...FULL_CROP_RECT };
};

const blankPrint = () => ({
  url: null,
  s3Key: null,
  name: null,
  size: null,
  isUploaded: false,
  progress: 0,
  comments: [],
});

const blankWeb = () => ({
  url: null,
  s3Key: null,
  size: null,
  generating: false,
  comments: [],
});

const hasPendingRevisionComment = (comments = []) =>
  comments.some(
    (c) =>
      c.status === "revisionSent" ||
      c.status === "revisionInProgress" ||
      (!c.status && !c.responseRevisionId && !c.responseRevisionUrl),
  );

/** @deprecated local label sort — prefer sortEditorShotsForDisplay */
const extractS3Key = (urlOrKey) => {
  if (!urlOrKey) return null;
  if (!urlOrKey.includes("://")) return urlOrKey;
  const parts = urlOrKey.split(".amazonaws.com/");
  if (parts[1]) return parts[1].split("?")[0];
  try {
    const u = new URL(urlOrKey);
    return u.pathname.replace(/^\//, "");
  } catch {
    return null;
  }
};

/**
 * Build capture rows from editor shots + existing edited photos.
 * One row per shot (Photo → Drone → Dusk & Golden Hour → Portraits).
 * No synthetic "extras portrait" slots.
 */
export const buildCaptureRows = ({
  item,
  existingCount = 0,
  editedWebPhotos = [],
  editedPrintPhotos = [],
  rawImages = [],
  editorShots = [],
  maxPhotoId = 0,
}) => {
  const editCount = item?.editCount || 0;
  // Photo → Drone → Golden Hour → Portraits
  const orderedShots = sortEditorShotsForDisplay(
    (editorShots || []).filter((s) => s?.mainImageId != null),
  );
  const shotCount = orderedShots.length;
  const primarySlots = Math.max(editCount, shotCount);
  const totalSlots = primarySlots;

  const sortedPrints = [...(editedPrintPhotos || [])].sort(
    (a, b) => Number(a.id) - Number(b.id),
  );
  const sortedWebs = [...(editedWebPhotos || [])].sort(
    (a, b) => Number(a.id) - Number(b.id),
  );
  const webById = new Map(
    sortedWebs.map((p) => [Number(p.id), p]),
  );
  const printById = new Map(
    sortedPrints.map((p) => [Number(p.id), p]),
  );

  const usedIds = new Set(
    [...sortedPrints, ...sortedWebs]
      .map((p) => Number(p.id))
      .filter((id) => Number.isFinite(id) && id > 0),
  );
  let nextNewId = Math.max(0, Number(maxPhotoId) || 0) + 1;
  const allocateId = () => {
    while (usedIds.has(nextNewId)) nextNewId += 1;
    const id = nextNewId;
    usedIds.add(id);
    nextNewId += 1;
    return id;
  };

  // Prints already linked to a shot via printPhotoId
  const printIdsClaimedByShots = new Set(
    orderedShots
      .map((s) => Number(s.printPhotoId))
      .filter((id) => Number.isFinite(id) && id > 0),
  );
  const unlinkedPrints = sortedPrints.filter(
    (p) => !printIdsClaimedByShots.has(Number(p.id)),
  );
  // Never reuse a captureId across rows — duplicates make upload/UI
  // updates hit every matching slot (e.g. Shot 1 + Shot 8).
  const claimedCaptureIds = new Set();

  return Array.from({ length: totalSlots }, (_, index) => {
    const shot = orderedShots[index] || null;

    let existingPrint = null;
    if (shot?.printPhotoId != null && printById.has(Number(shot.printPhotoId))) {
      existingPrint = printById.get(Number(shot.printPhotoId));
    } else {
      // Only consume an unlinked print if this slot's index still maps
      // cleanly and that print id isn't already claimed by another row.
      const candidate = unlinkedPrints[index] || null;
      if (
        candidate &&
        !claimedCaptureIds.has(Number(candidate.id))
      ) {
        existingPrint = candidate;
      }
    }

    // Web must follow its print id only — never assign sortedWebs[index]
    // to an empty primary slot (that leaked Shot 8's web onto Shot 1).
    const existingWeb = existingPrint
      ? webById.get(Number(existingPrint.id)) || null
      : null;

    let captureId = existingPrint
      ? Number(existingPrint.id)
      : allocateId();
    if (claimedCaptureIds.has(captureId)) {
      captureId = allocateId();
    }
    claimedCaptureIds.add(captureId);

    // Source = HDR/override (or AI input preview) — never AI output.
    // Print may be the AI image when "Use as print master" is clicked.
    let rawImage = null;
    if (shot) {
      const source = getShotSourceImage(shot);
      if (source?.url) {
        rawImage = {
          id: shot.shotId,
          name: shot.label || `Shot ${index + 1}`,
          url: source.url,
          type: "SHOT",
          shotId: shot.shotId,
          category: shot.category,
          eventTypeKey: shot.eventTypeKey,
        };
      }
    }

    let status = "empty";
    const printComments = existingPrint?.comments || [];
    const webComments = existingWeb?.comments || [];
    if (
      hasPendingRevisionComment(printComments) ||
      hasPendingRevisionComment(webComments)
    ) {
      status = "revision";
    } else if (existingPrint?.url && existingWeb?.url) status = "ready";
    else if (existingPrint?.url) status = "ready";

    return {
      captureId,
      index,
      isExtra: false,
      shotId: shot?.shotId || null,
      rawImage,
      print: existingPrint
        ? {
            url: existingPrint.url,
            s3Key:
              existingPrint.s3Key || extractS3Key(existingPrint.url),
            name: existingPrint.name,
            size: existingPrint.size,
            isUploaded: true,
            progress: 100,
            comments: printComments,
          }
        : blankPrint(),
      web: existingWeb
        ? {
            url: existingWeb.url,
            s3Key: existingWeb.s3Key || extractS3Key(existingWeb.url),
            size: existingWeb.size,
            generating: false,
            comments: webComments,
          }
        : blankWeb(),
      cropRect: resolveCropRect(existingWeb, existingPrint),
      cropAdjusted: isAdjustedCropRect(
        resolveCropRect(existingWeb, existingPrint),
      ),
      status,
      version: 1,
    };
  });
};

/**
 * Hook: print-only upload → S3 → generate web → send to review.
 *
 * @param {object} opts
 * @param {object} opts.api - { uploadPrint, generateWebFromPrint, sendToReview }
 */
export default function useEditorPrintUpload({
  item,
  propertyId,
  existingCount = 0,
  editedWebPhotos = [],
  editedPrintPhotos = [],
  rawImages = [],
  editorShots = [],
  commentedRawPhotos = [],
  maxPhotoId = 0,
  allocatePhotoId,
  api,
  onReviewSuccess,
}) {
  const editCount = item?.editCount || 0;
  const isExtrasBatch = isExtrasRawBatch(item);
  const isPhoneBatch = isPhoneRawBatch(item);

  const [rows, setRows] = useState(() =>
    buildCaptureRows({
      item,
      existingCount,
      editedWebPhotos,
      editedPrintPhotos,
      rawImages,
      editorShots,
      maxPhotoId,
    }),
  );
  const [cropIndex, setCropIndex] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isProcessingBatch, setIsProcessingBatch] = useState(false);

  useEffect(() => {
    setRows((prev) => {
      const built = buildCaptureRows({
        item,
        existingCount,
        editedWebPhotos,
        editedPrintPhotos,
        rawImages,
        editorShots,
        maxPhotoId,
      });

      if (!prev.length) return built;

      const prevIds = prev.map((r) => r.captureId).join(",");
      const nextIds = built.map((r) => r.captureId).join(",");
      if (prevIds !== nextIds) return built;

      // Preserve local upload/generate state when parent props refresh
      // (e.g. rawImages URL updates from the RAW renderer).
      return built.map((row) => {
        const local = prev.find((r) => r.captureId === row.captureId);
        if (!local) return row;

        const localBusy =
          local.web?.generating ||
          local.status === "uploading" ||
          (local.print?.url && !local.print?.isUploaded) ||
          (local.print?.progress > 0 && local.print?.progress < 100);

        const localReadyNotOnServer =
          local.print?.isUploaded &&
          local.web?.url &&
          !row.print?.url;

        // Keep a just-replaced print/web until parent props catch up
        // (same capture id may still show the old URL/s3Key).
        const localPrintNewer =
          !!local.print?.isUploaded &&
          !!local.print?.s3Key &&
          local.print.s3Key !== row.print?.s3Key;
        const localWebNewer =
          !!local.web?.s3Key &&
          local.web.s3Key !== row.web?.s3Key;

        if (localBusy || localReadyNotOnServer || localPrintNewer || localWebNewer) {
          return {
            ...row,
            print: {
              ...local.print,
              comments: local.print?.comments ?? row.print?.comments ?? [],
            },
            web: {
              ...local.web,
              comments: local.web?.comments ?? row.web?.comments ?? [],
            },
            status: local.status,
            cropRect: local.cropRect,
            cropAdjusted: local.cropAdjusted,
          };
        }

        return {
          ...row,
          print: row.print?.url
            ? {
                ...row.print,
                comments: row.print?.comments ?? local.print?.comments ?? [],
              }
            : local.print,
          web: row.web?.url
            ? {
                ...row.web,
                comments: row.web?.comments ?? local.web?.comments ?? [],
              }
            : local.web,
          status:
            hasPendingRevisionComment(row.print?.comments) ||
            hasPendingRevisionComment(row.web?.comments)
              ? "revision"
              : row.print?.url && row.web?.url
                ? "ready"
                : row.print?.url || local.print?.url
                  ? local.status === "ready"
                    ? "ready"
                    : local.status
                  : row.status,
          cropRect: local.cropAdjusted ? local.cropRect : row.cropRect,
          cropAdjusted: local.cropAdjusted || row.cropAdjusted,
        };
      });
    });
  }, [
    item?.eventId,
    item?.status,
    item?.editCount,
    existingCount,
    editedWebPhotos,
    editedPrintPhotos,
    rawImages,
    editorShots,
    maxPhotoId,
  ]);

  const revisionRequestItems = useMemo(
    () =>
      buildRevisionRequestItems(
        commentedRawPhotos,
        rawImages,
        item?.images,
        {},
      ),
    [commentedRawPhotos, rawImages, item?.images],
  );

  const printCount = rows.filter((r) => r.print?.isUploaded || r.print?.url)
    .length;
  const webCount = rows.filter((r) => r.web?.url && !r.web?.generating).length;
  const total = rows.length;
  const generatingCount = rows.filter((r) => r.web?.generating).length;

  const updateRow = useCallback((captureId, patch, shotId = null) => {
    setRows((prev) =>
      prev.map((r) => {
        const matchesShot =
          shotId != null &&
          r.shotId != null &&
          String(r.shotId) === String(shotId);
        const matchesCapture =
          Number(r.captureId) === Number(captureId) &&
          (shotId == null || r.shotId == null || matchesShot);
        // Prefer shot identity when known so duplicate captureIds can't
        // flash progress onto another slot.
        if (shotId != null) return matchesShot ? { ...r, ...patch } : r;
        return matchesCapture ? { ...r, ...patch } : r;
      }),
    );
  }, []);

  const patchRowNested = useCallback((captureId, key, nested, shotId = null) => {
    setRows((prev) =>
      prev.map((r) => {
        const matchesShot =
          shotId != null &&
          r.shotId != null &&
          String(r.shotId) === String(shotId);
        if (shotId != null) {
          return matchesShot
            ? { ...r, [key]: { ...r[key], ...nested } }
            : r;
        }
        return Number(r.captureId) === Number(captureId)
          ? { ...r, [key]: { ...r[key], ...nested } }
          : r;
      }),
    );
  }, []);

  const acceptPrintAndGenerate = useCallback(
    async ({ row, file, previewUrl }) => {
      if (!api?.uploadPrint || !api?.generateWebFromPrint) {
        showToast("error", "Upload API not configured");
        return;
      }

      const mb = file.size / (1024 * 1024);
      if (mb < MIN_PRINT_MB) {
        showToast(
          "error",
          `Print images must be >= 5MB. "${file.name}" is ${mb.toFixed(2)}MB.`,
        );
        return;
      }

      const alreadyPersisted = (editedPrintPhotos || []).some(
        (photo) => Number(photo.id) === Number(row.captureId),
      );
      const captureId =
        alreadyPersisted || typeof allocatePhotoId !== "function"
          ? row.captureId
          : allocatePhotoId();
      const previousCaptureId = row.captureId;
      const rowShotId = row.shotId || null;
      const printComments = row.print?.comments || [];
      const webComments = row.web?.comments || [];
      const previousPrint = row.print ? { ...row.print } : blankPrint();
      const previousWeb = row.web ? { ...row.web } : blankWeb();
      const previousStatus = row.status;
      const previousCropRect = row.cropRect;
      const previousCropAdjusted = row.cropAdjusted;
      const wasRevision =
        hasPendingRevisionComment(printComments) ||
        hasPendingRevisionComment(webComments);

      setRows((prev) =>
        prev.map((r) => {
          const matchesShot =
            rowShotId != null &&
            r.shotId != null &&
            String(r.shotId) === String(rowShotId);
          const matchesCapture =
            Number(r.captureId) === Number(previousCaptureId);
          if (rowShotId != null ? !matchesShot : !matchesCapture) return r;
          return {
            ...r,
            captureId,
            print: {
              url: previewUrl || URL.createObjectURL(file),
              s3Key: null,
              name: file.name,
              size: file.size,
              isUploaded: false,
              progress: 1,
              comments: printComments,
            },
            web: { ...blankWeb(), comments: webComments },
            status: "uploading",
          };
        }),
      );

      try {
        const uploaded = await api.uploadPrint(file, {
          propertyId,
          eventId: item?.eventId,
          captureId,
          onUploadProgress: (pct) => {
            patchRowNested(
              captureId,
              "print",
              { progress: Math.max(1, pct) },
              rowShotId,
            );
          },
        });

        updateRow(
          captureId,
          {
            print: {
              url: uploaded.url || previewUrl,
              s3Key: uploaded.s3Key,
              name: file.name,
              size: file.size,
              isUploaded: true,
              progress: 100,
              comments: printComments,
            },
            web: { ...blankWeb(), generating: true, comments: webComments },
            status: "generating",
          },
          rowShotId,
        );

        const gen = await api.generateWebFromPrint({
          propertyId,
          eventId: item?.eventId,
          captureId,
          printS3Key: uploaded.s3Key,
          cropRect: row.cropRect || FULL_CROP_RECT,
          fileNameHint: file.name,
        });

        updateRow(
          captureId,
          {
            print: {
              url: uploaded.url || previewUrl,
              s3Key: uploaded.s3Key,
              name: file.name,
              size: file.size,
              isUploaded: true,
              progress: 100,
              comments: printComments,
            },
            web: {
              url: gen.web.url,
              s3Key: gen.web.s3Key,
              size: gen.web.size,
              generating: false,
              comments: webComments,
            },
            cropRect: gen.cropRect || row.cropRect || FULL_CROP_RECT,
            cropAdjusted: isAdjustedCropRect(
              gen.cropRect || row.cropRect || FULL_CROP_RECT,
            ),
            status: wasRevision ? "revision" : "ready",
          },
          rowShotId,
        );

        // Persist draft so reload keeps print/web without completing the batch.
        try {
          if (api.persistEditedPhotos || api.sendToReview) {
            const persist = api.persistEditedPhotos || api.sendToReview;
            const cropRect = gen.cropRect || row.cropRect || FULL_CROP_RECT;
            await persist({
              propertyId,
              eventId: item?.eventId,
              persistOnly: true,
              editedPrintPhotos: [
                {
                  id: captureId,
                  name: file.name,
                  size: file.size,
                  s3Key: uploaded.s3Key,
                  eventId: item?.eventId,
                  webCropRect: cropRect,
                  ...shotFields(row),
                },
              ],
              editedWebPhotos: [
                {
                  id: captureId,
                  name:
                    (file.name || "image").replace(/\.[^.]+$/, "") + "-web.jpg",
                  size: gen.web.size,
                  s3Key: gen.web.s3Key,
                  eventId: item?.eventId,
                  webCropRect: cropRect,
                  ...shotFields(row),
                },
              ],
            });
          }
          showToast("success", "Print master saved");
        } catch (persistErr) {
          console.error(persistErr);
          showToast(
            "error",
            persistErr?.response?.data?.message ||
              persistErr.message ||
              "Uploaded, but failed to save — click Send to Review",
          );
        }
        return captureId;
      } catch (err) {
        console.error(err);
        showToast(
          "error",
          err?.response?.data?.message || err.message || "Upload failed",
        );
        // Restore the previous print/web — do not wipe a successful master
        // when Replace fails mid-flight (upload or web generation).
        updateRow(
          captureId,
          {
            captureId: previousCaptureId,
            print: previousPrint,
            web: previousWeb,
            cropRect: previousCropRect,
            cropAdjusted: previousCropAdjusted,
            status: previousStatus,
          },
          rowShotId,
        );
        return null;
      }
    },
    [
      api,
      propertyId,
      item?.eventId,
      editedPrintPhotos,
      allocatePhotoId,
      updateRow,
      patchRowNested,
    ],
  );

  const handleFilesForRow = useCallback(
    async (rowIndex, fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return null;
      const row = rows[rowIndex];
      if (!row) return null;
      const file = files[0];
      const previewUrl = URL.createObjectURL(file);
      return acceptPrintAndGenerate({ row, file, previewUrl });
    },
    [rows, acceptPrintAndGenerate],
  );

  const handleBulkFiles = useCallback(
    async (fileList) => {
      const files = Array.from(fileList || []);
      if (!files.length) return;

      setIsProcessingBatch(true);
      try {
        let fileIdx = 0;
        for (let i = 0; i < rows.length && fileIdx < files.length; i++) {
          const row = rows[i];
          if (row.print?.isUploaded || row.print?.url) continue;
          const file = files[fileIdx++];
          const previewUrl = URL.createObjectURL(file);
          const mb = file.size / (1024 * 1024);
          if (mb < MIN_PRINT_MB) {
            showToast(
              "error",
              `Skipped "${file.name}" — print must be >= 5MB (${mb.toFixed(2)}MB)`,
            );
            URL.revokeObjectURL(previewUrl);
            continue;
          }
          await acceptPrintAndGenerate({ row, file, previewUrl });
        }
      } finally {
        setIsProcessingBatch(false);
      }
    },
    [rows, acceptPrintAndGenerate],
  );

  const saveCrop = useCallback(
    async (rowIndex, cropRect) => {
      const row = rows[rowIndex];
      if (!row?.print?.s3Key && !row?.print?.url) {
        showToast("error", "Upload a print master before adjusting crop");
        return;
      }
      const printS3Key =
        row.print.s3Key ||
        (row.print.url || "").split(".amazonaws.com/")[1]?.split("?")[0];
      if (!printS3Key) {
        showToast("error", "Missing print S3 key — re-upload print master");
        return;
      }

      const nextRect = cropRect || FULL_CROP_RECT;
      updateRow(row.captureId, {
        cropRect: nextRect,
        cropAdjusted: isAdjustedCropRect(nextRect),
        web: { ...row.web, generating: true },
      });
      setCropIndex(null);

      try {
        const gen = await api.generateWebFromPrint({
          propertyId,
          eventId: item?.eventId,
          captureId: row.captureId,
          printS3Key,
          cropRect: nextRect,
          fileNameHint: row.print.name,
        });
        updateRow(row.captureId, {
          web: {
            url: gen.web.url,
            s3Key: gen.web.s3Key,
            size: gen.web.size,
            generating: false,
          },
          cropRect: gen.cropRect || nextRect,
          cropAdjusted: isAdjustedCropRect(gen.cropRect || nextRect),
          status: "ready",
        });
        showToast("success", "Web crop updated");
      } catch (err) {
        showToast(
          "error",
          err?.response?.data?.message || err.message || "Crop failed",
        );
        patchRowNested(row.captureId, "web", { generating: false });
      }
    },
    [rows, api, propertyId, item?.eventId, updateRow, patchRowNested],
  );

  const sendToReview = useCallback(async () => {
    const ready = rows.filter((r) => {
      const printKey = r.print?.s3Key || extractS3Key(r.print?.url);
      const webKey = r.web?.s3Key || extractS3Key(r.web?.url);
      return (
        (r.print?.isUploaded || r.print?.url) &&
        printKey &&
        r.web?.url &&
        webKey
      );
    });

    const requiredPrint = editCount;
    if (ready.length < requiredPrint) {
      showToast(
        "error",
        `Upload at least ${requiredPrint} print masters (and wait for web generation).`,
      );
      return;
    }

    const incomplete = ready.some((r) => r.web?.generating);
    if (incomplete || generatingCount > 0) {
      showToast("error", "Wait for all web versions to finish generating.");
      return;
    }

    const mapPrint = ready.map((r) => ({
      id: r.captureId,
      name: r.print.name,
      size: r.print.size,
      s3Key: r.print.s3Key || extractS3Key(r.print.url),
      eventId: item?.eventId,
      webCropRect: r.cropRect || FULL_CROP_RECT,
      ...shotFields(r),
    }));
    const mapWeb = ready.map((r) => ({
      id: r.captureId,
      name: (r.print.name || "image").replace(/\.[^.]+$/, "") + "-web.jpg",
      size: r.web.size,
      s3Key: r.web.s3Key || extractS3Key(r.web.url),
      eventId: item?.eventId,
      webCropRect: r.cropRect || FULL_CROP_RECT,
      ...shotFields(r),
    }));

    try {
      setIsSending(true);
      await api.sendToReview({
        propertyId,
        eventId: item?.eventId,
        editedWebPhotos: mapWeb,
        editedPrintPhotos: mapPrint,
      });
      showToast("success", "Sent to review");
      if (onReviewSuccess) onReviewSuccess();
      else window.location.reload();
    } catch (err) {
      showToast(
        "error",
        err?.response?.data?.message || err.message || "Send failed",
      );
    } finally {
      setIsSending(false);
    }
  }, [
    rows,
    editCount,
    generatingCount,
    item?.status,
    item?.eventId,
    api,
    propertyId,
    onReviewSuccess,
  ]);

  const canSend =
    printCount >= editCount &&
    webCount >= editCount &&
    generatingCount === 0 &&
    !isSending &&
    !isProcessingBatch;

  return {
    rows,
    printCount,
    webCount,
    total,
    generatingCount,
    extras: 0,
    editCount,
    portraits: 0,
    isExtrasBatch,
    isPhoneBatch,
    cropIndex,
    setCropIndex,
    isSending,
    isProcessingBatch,
    canSend,
    revisionRequestItems,
    handleBulkFiles,
    handleFilesForRow,
    saveCrop,
    sendToReview,
  };
}
